/**
 * Translation for code that runs outside React.
 *
 * The estimate output is built by plain modules — renderModel.ts (column
 * descriptors), pdf.ts (jsPDF drawing), csv.ts (file export) and
 * configDataPoints.ts (shared screen/PDF detail lists). None of them can call
 * useTranslation(), but all of them emit text the user reads.
 *
 * They read the live i18next instance instead. That is sound for their call
 * pattern: each one runs either during a React render or from a click handler,
 * so the active language is always the one on screen at that moment.
 *
 * The one obligation this places on callers: a component that renders what
 * these modules return must itself subscribe with useTranslation(), or a
 * language switch will not re-run them and the output will keep the previous
 * language until something else forces a render. Every current caller does.
 *
 * Deliberately a thin named wrapper rather than importing i18next directly at
 * each site — it makes "translated text produced outside React" greppable, and
 * gives one place to change if this ever needs to take an explicit locale
 * (e.g. rendering a PDF in the estimate's saved language rather than the
 * viewer's, which the estimate-locale work will want).
 */

import i18next from './index';
import { SOURCE_LOCALE } from '../config/locales';

/**
 * Translate a fully-qualified `namespace:key` outside a React component.
 *
 * Before i18next has initialized, `i18next.t()` returns an empty string — which
 * would silently produce a Bill of Materials with blank column headings rather
 * than an obvious failure. So an uninitialized instance returns the key itself
 * instead, matching how i18next renders a missing key and leaving something
 * searchable on screen. In the app this should never be reached: LanguageProvider
 * holds paint until init resolves, and every caller runs after first paint.
 */
export function tr(key: string, options?: Record<string, unknown>): string {
  if (!i18next.isInitialized) return key;
  return i18next.t(key, options) as string;
}

/**
 * The active locale, or null when it is the source language.
 *
 * Used by the API service to decide whether to send `?locale=` on the content
 * endpoints. Null rather than 'en' on purpose: the server treats a missing
 * parameter as "serve the base rows" and skips every translation query, so not
 * sending it is both correct and the fast path.
 *
 * Read at call time rather than passed down: these fetchers run from effects
 * and click handlers scattered across the app, and the language is global. The
 * callers' obligation is to re-run when it changes — ProductsBootGate,
 * SiteExperienceProvider and the geo/category loaders all list it as an effect
 * dependency for that reason.
 */
export function contentLocale(): string | null {
  if (!i18next.isInitialized) return null;
  const lng = i18next.language;
  if (!lng || lng === SOURCE_LOCALE) return null;
  return lng;
}

/**
 * The active locale, always — including the source language.
 *
 * The mirror of contentLocale(): where that one answers "does the server need
 * to translate anything for this request", this answers "what language is the
 * user working in". For a saved estimate that distinction matters, because
 * "this was built in English" is a fact worth recording. Collapsing it to null
 * would leave the estimate unmarked and its share link rendering in whatever
 * language the recipient's browser prefers.
 */
export function activeLocale(): string {
  if (!i18next.isInitialized) return SOURCE_LOCALE;
  return i18next.language || SOURCE_LOCALE;
}

/** Append `?locale=` / `&locale=` to a URL when the active locale isn't English. */
export function withLocale(url: string): string {
  const lng = contentLocale();
  if (!lng) return url;
  return `${url}${url.includes('?') ? '&' : '?'}locale=${encodeURIComponent(lng)}`;
}
